import { useForm } from "@tanstack/react-form";
import { Link } from "@tanstack/react-router";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Switch } from "~/components/ui/switch";
import {
  type ConsentGroups,
  FULL_CONSENT,
  NO_CONSENT,
} from "~/lib/shared/consent";

/**
 * What each group holds, as the Privacy Policy's cookie section names it.
 * A tool listed here must be held back by its group.
 */
const CATEGORIES = {
  essential: {
    title: "Essential",
    description:
      "Needed to sign you in, keep your progress in quizzes and forms, and keep the site working and safe.",
    includes: "sign-in, saved quiz progress, error reports, invitation links.",
  },
  analytics: {
    title: "Analytics",
    description:
      "Help us see which pages and features work, and find problems to fix.",
    includes:
      "Google Analytics, Microsoft Clarity (screen recordings and heatmaps), Ahrefs Analytics, Cloudflare Web Analytics, SiteBehaviour, visit counts in our page builder (Builder.io), recordings of your screen when an error occurs, saved campaign tags, and page tests.",
  },
  marketing: {
    title: "Marketing",
    description:
      "Help us measure our ads and credit the partners who refer you.",
    includes: "Meta Pixel, GoHighLevel partner tracking.",
  },
} as const;

type Category = keyof typeof CATEGORIES;

/**
 * "Manage choices" and "Cookie settings": Essential is always on, and the
 * switches start from what may run now, which is the saved choice when there
 * is one. Escape closes it without saving.
 */
export function CookieSettingsDialog({
  open,
  onOpenChange,
  allowed,
  pending,
  onSave,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly allowed: ConsentGroups;
  readonly pending: boolean;
  readonly onSave: (groups: ConsentGroups) => void;
}): React.JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        {/* Mounted per opening, so the switches restart from `allowed`. */}
        <CookieSettingsForm
          allowed={allowed}
          pending={pending}
          onSave={onSave}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function CookieSettingsForm({
  allowed,
  pending,
  onSave,
  onClose,
}: {
  readonly allowed: ConsentGroups;
  readonly pending: boolean;
  readonly onSave: (groups: ConsentGroups) => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const form = useForm({
    defaultValues: {
      analytics: allowed.analytics,
      marketing: allowed.marketing,
    },
    onSubmit: ({ value }) => onSave(value),
  });

  return (
    <form
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <DialogHeader>
        <DialogTitle>Cookie settings</DialogTitle>
        <DialogDescription>
          Choose which tools can run on this site. Your choice is saved on this
          device for 12 months. You can change it at any time from the Cookie
          settings link.
        </DialogDescription>
      </DialogHeader>
      <ConsentCategory
        category="essential"
        checked
        disabled
        stateLabel="Always on"
      />
      <form.Field name="analytics">
        {(field) => (
          <ConsentCategory
            category="analytics"
            checked={field.state.value}
            onCheckedChange={field.handleChange}
          />
        )}
      </form.Field>
      <form.Field name="marketing">
        {(field) => (
          <ConsentCategory
            category="marketing"
            checked={field.state.value}
            onCheckedChange={field.handleChange}
          />
        )}
      </form.Field>
      <p className="text-sm text-muted-foreground">
        Turning a tool off reloads the page.{" "}
        <Link
          to="/page/$name"
          params={{ name: "privacy-policy" }}
          className="text-purple-700 underline underline-offset-2 hover:text-purple-900"
          onClick={onClose}
        >
          Read our Privacy Policy
        </Link>
      </p>
      <DialogFooter>
        <Button
          type="button"
          disabled={pending}
          onClick={() => onSave(NO_CONSENT)}
        >
          Reject all
        </Button>
        <Button
          type="button"
          disabled={pending}
          onClick={() => onSave(FULL_CONSENT)}
        >
          Accept all
        </Button>
        <Button type="submit" variant="primary" disabled={pending}>
          Save choices
        </Button>
      </DialogFooter>
    </form>
  );
}

function ConsentCategory({
  category,
  checked,
  disabled = false,
  onCheckedChange,
  stateLabel = checked ? "On" : "Off",
}: {
  readonly category: Category;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly onCheckedChange?: (checked: boolean) => void;
  readonly stateLabel?: string;
}): React.JSX.Element {
  const { title, description, includes } = CATEGORIES[category];
  const switchId = `cookie-category-${category}`;
  const descriptionId = `${switchId}-description`;

  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 p-4">
      <div className="grid gap-1">
        <Label htmlFor={switchId} className="text-gray-900">
          {title}
        </Label>
        <p id={descriptionId} className="text-sm text-gray-700">
          {description}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          <span className="font-medium text-gray-700">Includes:</span>{" "}
          {includes}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-center gap-1">
        <Switch
          id={switchId}
          checked={checked}
          disabled={disabled}
          onCheckedChange={onCheckedChange}
          aria-describedby={descriptionId}
        />
        {/* The switch announces its own state. */}
        <span aria-hidden="true" className="text-xs text-muted-foreground">
          {stateLabel}
        </span>
      </div>
    </div>
  );
}
